import { IconButton, SkeletonBox } from '@eduact/ed-components'
import { RaisedButton } from '@eduact/ed-components'
import { CTAImage } from '@Features/LandingPage/Assets/Images'
import CTA_Image from '@Features/LandingPage/GuestLanding/Assets/cta.png'
import { GridItem, GridLayout } from '@eduact/ed-system'
import React from 'react'
import { RiParentFill } from 'react-icons/ri'
import styled from 'styled-components'
import { Typography } from '@eduact/ed-system'
import CustomLink from '@Styles/CustomLink'
import routes from '@Constants/Routes'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { defaultAssets } from '@Constants/assets.defaults'
import { trackEvent } from '@Utilities/marketingTags'

const CTASection = () => {
	const navigate = useNavigate()

	const handleCta = (ctaText: string, route: string) => {
		trackEvent('CTAClick', { cta_text: ctaText, cta_location: 'hero' })
		navigate(route)
	}
	const { t } = useTranslation(['landingPage', 'generic', 'login'])

	const { assets, assetsLoading, landing_headline, landing_subheadline, classrooms_enabled, standalone_courses_enabled } = useAppSelector(
		s => s.settings
	)
	const { t: tCourses } = useTranslation('courses')

	t('landing_page.cta_title')

	/* Rendered twice — inside the hero band for tablet/desktop, on the plain
	   strip under the photo for phones (the wrappers toggle at 770px). */
	const heroActions = (
		<>
			<RaisedButton
				onClick={() => handleCta('login', routes.login)}
				// maxWidth={{ sm: '6.313rem', lg: '' }}
				minWidth={{ sm: 'unset', lg: '12rem' }}
				btnSize="small"
			>
				{t('login', { ns: 'login' })}
			</RaisedButton>
			<RaisedButton
				onClick={() => handleCta('signup', routes.signup)}
				// maxWidth={{ sm: '6.313rem', lg: '' }}
				minWidth={{ sm: 'unset', lg: '12rem' }}
				bgFallback
				btnSize="small"
				outlined
			>
				{t('signup', { ns: 'login' })}
			</RaisedButton>
			{!classrooms_enabled && standalone_courses_enabled && (
				<RaisedButton
					onClick={() => handleCta('courses', routes.courses)}
					minWidth={{ sm: 'unset', lg: '12rem' }}
					btnSize="small"
					variant="princetonOrange"
				>
					{tCourses('title')}
				</RaisedButton>
			)}
			{/* <GridItem alignItems="center" gridColumn={'1 / 3'}>
			<RaisedButton minWidth={{ lg: '12rem' }} btnSize="small" variant="princetonOrange">
				<IconButton variant="light" mx={10} icon={<RiParentFill />} />
				Parent
			</RaisedButton>
		</GridItem> */}
		</>
	)

	return (
		<>
			{assetsLoading && <SkeletonBox width="100%" height="40vh" isLoading />}
			{!assetsLoading && (
				<>
					<SectionWrapper
						background={assets.landing_main ?? defaultAssets.landing_main}
						mobileBackground={assets.landing_main_mobile}
					>
						<SectionContent>
							<SectionTitle>
								{landing_headline ?? t('cta_title')}{' '}
								<Typography fontWeight={'bold'}>{!landing_headline && t('you', { ns: 'generic' })}</Typography>
							</SectionTitle>
							<SectionSubTitle>{landing_subheadline ?? t('cta_sub_title')}</SectionSubTitle>

							<ActionsWrapper>{heroActions}</ActionsWrapper>
						</SectionContent>
					</SectionWrapper>
					<MobileActionsWrapper>{heroActions}</MobileActionsWrapper>
				</>
			)}
		</>
	)
}

export default CTASection

const SectionWrapper = styled.div<{ background: string; mobileBackground?: string }>`
	/* background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${props => props.background}); */
	background: url(${props => props.background});

	/* Both axes, not just x: whenever cover still has excess to crop (phones,
	   off-spec uploads), it trims both edges evenly instead of always eating
	   the bottom (position-y otherwise stays at its initial 0% = top). */
	background-position: center;
	background-size: cover;
	padding: 1.5rem;
	padding: 3.375rem 2.625rem;
	/* 16/5 = the documented 2560x800 hero spec, so an on-spec upload shows
	   in full: a content-sized band's ratio drifts with viewport width and
	   the root font-size steps, and cover cropped the difference. The asset
	   is a raw Settings upload (no cropper lock), so off-spec files still
	   crop — but symmetrically, per the position above. Phones keep the
	   content-sized band: 3.2:1 at their widths is too short for the copy,
	   and there is no mobile hero variant to match. The flex centering keeps
	   the copy mid-band now that the band outgrows its content. */
	/* Phones with a dedicated mobile crop show it whole in a box of its exact
	   4:3 ratio — a taller band than the content-sized fallback, with nothing
	   cropped (a plain vh floor was tried and rejected: any box taller than
	   the desktop asset's shape can only show a slice of it). Without the
	   upload, the band stays content-sized over the desktop asset's centre
	   crop, as before. */
	@media (max-width: 769px) {
		display: flex;
		flex-direction: column;
		justify-content: center;
		${p => (p.mobileBackground ? `background-image: url(${p.mobileBackground}); aspect-ratio: 4 / 3;` : '')}
	}
	@media (min-width: 770px) {
		aspect-ratio: 16 / 5;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}
	${({ theme }) => `${theme.mediaQueries.large}{
        padding: 5.813rem 12.688rem;
		padding-top :8rem;
    }`}
`
const SectionContent = styled.div`
	color: ${props => props.theme.colors.light};
	${({ theme }) => `${theme.mediaQueries.large}{
        width: 30%;
        
    }`}
`
const SectionTitle = styled.h2`
	font-size: 1.25rem;
	font-weight: 500;
	line-height: 1.2;
	margin-bottom: 0.5rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		font-size: 2.25rem;
		margin-bottom : 1rem;
        
    }`}
`
const SectionSubTitle = styled.h4`
	line-height: 1.2;
	font-size: 0.75rem;
	/* margin: 1.5rem 0rem; */
	margin-bottom: 2rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		font-size: 1.375rem;
		margin-bottom: 3rem;
        // margin: 1.5rem 2rem;
        
    }`}
`
const ActionsWrapper = styled.div`
	/* display: grid; */
	/* grid-template-columns: repeat(2, 1fr); */
	/* grid-template-rows: repeat(2, 1fr); */
	/* grid-gap: 1rem; */
	/* place-items: center; */
	display: flex;
	grid-gap: 0.625rem;
	/* Phones get the strip below the photo instead (MobileActionsWrapper). */
	@media (max-width: 769px) {
		display: none;
	}
	${({ theme }) => `${theme.mediaQueries.large}{
		grid-gap: 1rem;
		/* Larger CTAs sitting in the band's lower third on desktop — the
		   descendant rule outweighs the buttons' own btnSize="small"/minWidth
		   styling, which the phone strip keeps at its small size. The margin
		   looks outsized because the band's flex centering absorbs half of
		   it; 10rem lands the row at ~74% of the band height. */
		margin-top: 10rem;
		> button {
			font-size: 1.125rem;
			padding: 1.25rem 2.5rem;
			min-width: 14rem;
			justify-content: center;
		}
        // margin: 0rem 4rem;
    }`}
`

/* The phone rendering of heroActions: a strip on the page background right
   under the photo band, Abwaab-style, instead of buttons floating over the
   people in the image. The buttons stay legible off the photo — login is
   variant-filled and the outlined signup carries bgFallback's white. */
const MobileActionsWrapper = styled.div`
	display: flex;
	grid-gap: 0.625rem;
	padding: 1.25rem 2.625rem;
	> button {
		flex: 1;
		justify-content: center;
	}
	@media (min-width: 770px) {
		display: none;
	}
`
